/* ============================================================================
   mz-add-to-cart.js — authoritative add-to-cart → cart-drawer bridge.

   The theme's product-form.js and the product-card quick-add handlers were
   submitting natively / bouncing add-to-cart to the /cart PAGE. This single
   document-level, CAPTURE-phase interceptor takes over add-to-cart everywhere:
   it does the AJAX add itself, opens the existing <cart-drawer> (the same open()
   the cart icon uses) and refreshes its contents, then runs the gift-tier
   reconcile. stopImmediatePropagation() prevents the theme handlers from also
   firing, so exactly ONE /cart/add.js request happens per click (no double-add,
   no native submit). Same-origin only. Loaded site-wide in <head> (deferred), so
   it is registered before any click and isn't subject to inline/section caching.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__mzATC) return;
  window.__mzATC = true;

  /* Universal add-to-cart button feedback: the checkmark draws in IMMEDIATELY on tap
     (optimistic — adds virtually never fail), contained inside the button (no text
     swap, no fly-out). Reverts to the button's original label afterwards. */
  function mzBtnFeedback(btn, state) {
    if (!btn) return;
    if (state === 'loading') {
      if (btn.__mzBusy) return;
      btn.__mzBusy = true;
      btn.__mzOrig = btn.innerHTML;
      btn.style.minWidth = btn.offsetWidth + 'px';
      btn.classList.add('mz-atc-anim');
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML = '<span class="mz-atc-tick" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4 12.5l5 5 11-11"/></svg></span>';
    } else if (state === 'done') {
      if (!btn.__mzBusy) return;
      clearTimeout(btn.__mzT);
      btn.__mzT = setTimeout(function () { mzBtnFeedback(btn, 'reset'); }, 1100);
    } else {
      clearTimeout(btn.__mzT);
      if (btn.__mzOrig != null) btn.innerHTML = btn.__mzOrig;
      btn.style.minWidth = '';
      btn.classList.remove('mz-atc-anim');
      btn.removeAttribute('aria-busy');
      btn.__mzBusy = false;
    }
  }
  window.mzBtnFeedback = mzBtnFeedback;

  function drawerEl() { return document.querySelector('cart-drawer'); }
  /* Auto-open policy: window.MZ_CART_OPEN_EVERY = N (from theme settings).
     0/absent → never auto-open; 1 → every add; N → the Nth, 2Nth… add of the
     session. Manual opens (cart icon / dock) are unaffected. */
  function openDrawer() {
    var cd = drawerEl();
    if (!cd || typeof cd.open !== 'function') return;
    var every = window.MZ_CART_OPEN_EVERY;
    if (typeof every !== 'number') every = window.MZ_CART_NO_AUTOOPEN ? 0 : 1; // legacy flag fallback
    if (!every || every < 1) return;
    var n = 0;
    try { n = parseInt(sessionStorage.getItem('mz_atc_n') || '0', 10) || 0; } catch (e) {}
    if (n % every !== 0) return;
    try { cd.open(cd); } catch (e) {} // pass a trigger so the open isn't treated as an unwanted auto-pop
  }

  /* Apply server-rendered sections to the live drawer + cart bubble WITHOUT going
     through Dawn's renderContents (which force-opens and can throw). Prefer the
     drawer snippet's own renderer if present (keeps a single code path). */
  function applySections(sections) {
    if (!sections) return;
    if (typeof window.mzApplyCartSections === 'function') { window.mzApplyCartSections(sections); return; }
    var live = document.getElementById('CartDrawer');
    if (live && sections['cart-drawer']) {
      try {
        var src = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        if (src) live.innerHTML = src.innerHTML;
      } catch (e) {}
    }
    var bub = document.getElementById('cart-icon-bubble');
    if (bub && sections['cart-icon-bubble']) {
      try {
        var d = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var sb = d.querySelector('#cart-icon-bubble') || d.querySelector('.shopify-section');
        if (sb) bub.innerHTML = sb.innerHTML;
      } catch (e) {}
    }
  }

  function reconcileGifts() {
    if (typeof window.mzReconcileGifts === 'function') { try { window.mzReconcileGifts(); } catch (e) {} }
  }

  /* Tell the sticky cart dock (and anything else listening) the cart changed, so its
     reward progress bar pops up and refreshes on every add. */
  function notifyCartUpdated() {
    try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (e) {}
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mz-add-to-cart' });
      }
    } catch (e) {}
  }

  /* Refresh the drawer + open it. Uses the add response sections when present,
     otherwise re-fetches them, then opens and reconciles. Never navigates. */
  function refreshAndOpen(addResponse) {
    // one successful add = one tick of the session counter (gifts don't pass here)
    try { sessionStorage.setItem('mz_atc_n', String((parseInt(sessionStorage.getItem('mz_atc_n') || '0', 10) || 0) + 1)); } catch (e) {}
    if (addResponse && addResponse.sections) {
      applySections(addResponse.sections);
      openDrawer();
      reconcileGifts();
      notifyCartUpdated();
      return Promise.resolve();
    }
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(base + '?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (s) { applySections(s); })
      .catch(function () {})
      .then(function () { openDrawer(); reconcileGifts(); notifyCartUpdated(); });
  }

  /* POST to /cart/add.js, retrying on HTTP 429 (Shopify cart throttle) with backoff.
     Rapid repeated adds otherwise pile up requests and the throttle would make adds
     silently stop working — the retry rides it out instead. */
  var mzOwnAdd = false;
  function addFetchRetry(opts, tries) {
    tries = tries || 0;
    mzOwnAdd = true;
    var p;
    try { p = fetch('/cart/add.js', opts); } finally { mzOwnAdd = false; }
    return p.then(function (r) {
      if (r.status === 429 && tries < 4) {
        var wait = 700 * Math.pow(2, tries); // 700, 1400, 2800, 5600 ms
        var ra = parseFloat(r.headers.get('Retry-After'));
        if (ra > 0) wait = Math.max(wait, ra * 1000);
        return new Promise(function (res) { setTimeout(res, wait); }).then(function () { return addFetchRetry(opts, tries + 1); });
      }
      return r;
    });
  }

  /* Merge touchpoint attribution + origin context into line-item properties, so each
     ordered item records where it came from. Safe no-op if tracking isn't loaded. */
  function trackProps(el, sourceType) {
    var extra = {};
    try {
      var sec = el && el.closest && el.closest('[data-track-section]');
      if (sec) extra._added_from = sec.getAttribute('data-track-section') || '';
      var up = el && el.closest && el.closest('product-recommendations, .cart-drawer__recommendations, [data-cp-upsell]');
      if (up && !sourceType) sourceType = 'upsell';
      if (sourceType) extra._source_type = sourceType;
    } catch (e) {}
    return (window.mzTrack && window.mzTrack.props) ? window.mzTrack.props(extra) : extra;
  }
  function beaconAdd(id, qty, sourceType, from) {
    try { if (window.mzTrack && window.mzTrack.send) window.mzTrack.send('add_to_cart', { variant: String(id || ''), qty: qty || 1, source_type: sourceType || '', added_from: from || '' }); } catch (e) {}
  }

  function addItems(items) {
    return addFetchRetry({
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
    }).then(function (r) { return r.json(); });
  }

  /* Surface an add error (sold out, required selling plan, etc.) in the product
     form's own error slot — never navigate. */
  function showToast(msg) {
    try {
      var t = document.createElement('div');
      t.textContent = msg;
      t.style.cssText = 'position:fixed;left:50%;bottom:84px;transform:translateX(-50%);background:#20222A;color:#fff;padding:12px 18px;border-radius:12px;font-size:14px;font-weight:600;z-index:100001;box-shadow:0 8px 24px rgba(0,0,0,.25);max-width:90vw;text-align:center';
      document.body.appendChild(t);
      setTimeout(function () { t.style.transition = 'opacity .3s'; t.style.opacity = '0'; setTimeout(function () { t.remove(); }, 300); }, 2600);
    } catch (e) {}
  }
  function showAddError(form, data) {
    var msg = (data && (data.description || data.message)) || 'Sorry, this item could not be added to the cart.';
    var wrap = form && form.querySelector('.product-form__error-message-wrapper');
    var el = wrap && wrap.querySelector('.product-form__error-message');
    if (wrap && el) { el.textContent = msg; wrap.removeAttribute('hidden'); }
    else { showToast(msg); }
  }

  /* ---- PDP / any <product-form> add-to-cart (form submit, capture phase) ---- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    var idInput = form.querySelector('[name="id"]');
    var isAdd = /\/cart\/add/.test(action) || (idInput && (form.closest('product-form') || form.getAttribute('data-type') === 'add-to-cart-form'));
    if (!isAdd) return;
    // Bundle-builder / quick-edit forms stage items client-side (no cart POST) — leave them to the theme.
    if (form.closest('product-form-bundle, product-form-quick-edit')) return;
    // let "Buy it now" / dynamic checkout submit through untouched
    if (e.submitter && e.submitter.getAttribute('name') && e.submitter.getAttribute('name') !== 'add') return;
    e.preventDefault();
    e.stopImmediatePropagation();

    /* Post the ENTIRE form — variant id, quantity, line-item properties, selling_plan,
       bundle data, everything — so we match the theme's native submit exactly. Sending
       a trimmed-down {id,quantity} drops required fields (e.g. a subscription selling
       plan) and Shopify answers 422. */
    var fd = new FormData(form);
    /* Card qty chips / steppers set a VISIBLE quantity input that lives OUTSIDE the
       form; the form itself carries a hidden quantity stuck at the minimum. Read the
       visible value at submit time or every first add posts quantity 1. */
    var fQty = 1;
    try {
      var qtyWrap = form.closest('.card__quantity-selector, .card-wrapper, .card') || form.closest('product-info, section');
      var visQty = qtyWrap && qtyWrap.querySelector('input[name="quantity"]:not([type="hidden"])');
      if (visQty && parseInt(visQty.value, 10) > 0) {
        fQty = parseInt(visQty.value, 10);
        fd.set('quantity', visQty.value);
        var hidQty = form.querySelector('input[name="quantity"]');
        if (hidQty) hidQty.value = visQty.value;
      } else if (fd.get('quantity')) {
        fQty = parseInt(fd.get('quantity'), 10) || 1;
      }
    } catch (e) {}
    fd.append('sections', 'cart-drawer,cart-icon-bubble');
    fd.append('sections_url', window.location.pathname);
    // Stamp touchpoint props onto this line item (PDP forms → 'pdp', collection-card forms → 'grid')
    var fSource = form.closest('.card-wrapper, .tsp-card, .ccg-card') ? 'grid' : 'pdp';
    var fProps = trackProps(form, fSource);
    for (var pk in fProps) { if (fProps.hasOwnProperty(pk)) fd.append('properties[' + pk + ']', fProps[pk]); }
    var fVariant = (form.querySelector('[name="id"]') || {}).value;
    var submitBtn = form.querySelector('[type="submit"]');
    // Animate the submit button AND the sticky bar's proxy button (whichever the
    // shopper is actually looking at) so the feedback is always visible.
    var stickyBtn = document.querySelector('[data-mz-sticky-add]');
    function fb(state) { mzBtnFeedback(submitBtn, state); if (stickyBtn) mzBtnFeedback(stickyBtn, state); }
    fb('loading');
    var t0 = Date.now();
    addFetchRetry({ method: 'POST', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (resp) {
        if (!resp.ok || (resp.data && resp.data.status)) { fb('reset'); showAddError(form, resp.data); return; }
        fb('done');
        beaconAdd(fVariant, fQty, fProps._source_type, fProps._added_from);
        /* Keep the tick on screen briefly before the drawer re-render replaces the button */
        var wait = Math.max(0, 650 - (Date.now() - t0));
        setTimeout(function () { refreshAndOpen(resp.data); }, wait);
      })
      .catch(function () { fb('reset'); });
  }, true);

  /* ---- Product-card quick-add buttons (collection / home / recommendations) ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest(
      '[data-tsp-add], [data-ccg-add], .tsp-card button[data-variant], .ccg-card button[data-variant], .tsp-card a[data-variant], .ccg-card a[data-variant]'
    );
    if (!btn) return;
    var id = btn.getAttribute('data-variant') || btn.getAttribute('data-variant-id');
    if (!id || btn.disabled) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    var card = btn.closest('.tsp-card, .ccg-card');
    var qty = 1;
    if (card) {
      var custom = card.querySelector('.tsp-chip-custom, .ccg-chip-custom');
      if (custom && custom.value) { var cv = parseInt(custom.value, 10); if (cv > 0) qty = cv; }
      if (qty === 1) { var active = card.querySelector('.tsp-chip.is-active, .ccg-chip.is-active'); if (active) { var av = parseInt(active.getAttribute('data-qty'), 10); if (av > 0) qty = av; } }
    }
    mzBtnFeedback(btn, 'loading');
    var t0 = Date.now();
    var tprops = trackProps(btn, 'grid');
    addItems([{ id: id, quantity: qty, properties: tprops }])
      .then(function (res) {
        if (res && res.status) { mzBtnFeedback(btn, 'reset'); showAddError(null, res); return; }  // rejected → show reason, don't open an empty drawer
        mzBtnFeedback(btn, 'done');
        beaconAdd(id, qty, tprops._source_type, tprops._added_from);
        // Keep the tick on screen briefly before the re-render replaces the button
        var wait = Math.max(0, 650 - (Date.now() - t0));
        setTimeout(function () { refreshAndOpen(res); }, wait);
      })
      .catch(function () { mzBtnFeedback(btn, 'reset'); });
  }, true);

  /* ---- Universal feedback shim: sections with their OWN add-to-cart JS (byob-builder,
     fan-favourites, collection bundles, card qty adds…) POST /cart/add themselves.
     Track the last tapped add-ish control; when any /cart/add fetch starts, tick it.
     Buttons already animated by the handlers above are skipped via __mzBusy. ---- */
  (function () {
    var lastBtn = null, lastT = 0;
    document.addEventListener('pointerdown', function (e) {
      var el = e.target.closest && e.target.closest('button, [role="button"], a, input[type="submit"]');
      if (!el) return;
      if (el.closest('[data-cp-qty], quantity-input, .quantity, [name="minus"], [name="plus"]')) return; // qty steppers aren't "add" taps
      var txt = ((el.getAttribute('aria-label') || '') + ' ' + (el.textContent || '') + ' ' + el.className).toLowerCase();
      if (/\badd\b|add to cart|add-to-cart|atc|\+/.test(txt) || el.hasAttribute('data-variant-id') || el.closest('[data-variant-id]')) {
        lastBtn = el; lastT = Date.now();
      }
    }, true);
    var origFetch = window.fetch;
    var foreignRefreshTimer = null;
    function foreignRefresh() {
      if (foreignRefreshTimer) clearTimeout(foreignRefreshTimer);
      foreignRefreshTimer = setTimeout(function () {
        var base = (window.routes && window.routes.cart_url) || '/cart';
        origFetch(base + '?sections=cart-drawer,cart-icon-bubble')
          .then(function (r) { return r.json(); })
          .then(function (s) { applySections(s); })
          .catch(function () {})
          .then(function () { reconcileGifts(); notifyCartUpdated(); });
      }, 400);
    }
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : ((input && input.url) || '');
      var isAdd = url.indexOf('/cart/add') > -1;
      var isOwn = mzOwnAdd;
      var btn = null;
      if (isAdd && lastBtn && Date.now() - lastT < 3000 && !lastBtn.__mzBusy) {
        btn = lastBtn;
        mzBtnFeedback(btn, 'loading');   // tick draws in immediately
      }
      var p = origFetch.apply(this, arguments);
      if (btn) {
        p.then(function (r) { if (r && r.ok) { mzBtnFeedback(btn, 'done'); } else { mzBtnFeedback(btn, 'reset'); } })
         .catch(function () { mzBtnFeedback(btn, 'reset'); });
      }
      /* Sections with their own add-to-cart JS (bundle CTAs, bulk orders, apps)
         inject into the old #minicart-form and leave the drawer stale — refresh
         it silently on every foreign add so the drawer is always current. */
      if (isAdd && !isOwn) {
        p.then(function (r) { if (r && r.ok) foreignRefresh(); }).catch(function () {});
      }
      return p;
    };
  })();

  /* ---- Replay adds that happened BEFORE this script loaded (queued by the
     inline guard in theme.liquid). Restores button state, adds via AJAX,
     opens/updates the drawer + dock exactly like a normal add. ---- */
  (function () {
    var q = window.__mzPendingAdds;
    if (!q || !q.length) return;
    q.splice(0).forEach(function (fd) {
      try {
        fd.append('sections', 'cart-drawer,cart-icon-bubble');
        fd.append('sections_url', window.location.pathname);
      } catch (e) {}
      addFetchRetry({ method: 'POST', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          document.querySelectorAll('[aria-busy="true"]').forEach(function (b) { b.removeAttribute('aria-busy'); b.style.opacity = ''; });
          if (data && data.status) { showAddError(null, data); return; }
          try { sessionStorage.setItem('mz_atc_n', String((parseInt(sessionStorage.getItem('mz_atc_n') || '0', 10) || 0) + 1)); } catch (e) {}
          refreshAndOpen(data);
        })
        .catch(function () {});
    });
  })();
})();
